import { Injectable } from '@angular/core';

import { PrimitiveType, Validations } from '../../class/workflow.type';
import { LogicFnContext } from '../logic/logic.service';
import { StatefulValue } from '../../class/stateful-value.class';

type ValidationFns = {
  [validationName in keyof Required<Validations>]: (
    _context: LogicFnContext,
    value: PrimitiveType,
    arg: Validations[keyof Validations]
  ) => boolean;
};

const DECIMAL_RX = /^[^.]*./;
const NUMERIC_ONLY_RX = /[^0-9]*/;
const LETTERS_ONLY_RX = /^[a-zA-Z][a-zA-z '-]*$/;
const DATE_VALID_RX =
  // eslint-disable-next-line max-len
  /^((0[13578]|1[02])[\/.](29|30|31)[\/.](19|20)[0-9]{2})|((01|0[3-9]|1[1-2])[\/.](29|30)[\/.](19|20)[0-9]{2})|((0[1-9]|1[0-2])[\/.](0[1-9]|1[0-9]|2[0-8])[\/.](19|20)[0-9]{2})|((02)[\/.]29[\/.](((19|20)(04|08|[2468][048]|[13579][26]))|2000))$/;

// Note: Using same regex as login widget but added a limit to the characters {1,75} to resolve ReDoS vulnerability
export const SENSIBLE_EMAIL_RX = /^[a-zA-Z0-9._%+-]{1,75}@[a-zA-Z0-9.-]{1,75}\.[a-zA-Z]{2,4}$/;

/**
 * Validation methods for every validation type on the Validations interface
 */
@Injectable({
  providedIn: 'root'
})
export class ValidationService implements ValidationFns {
  /**
   * Validate against a maximum number of decimal digits
   * @param allowedDecimalDigits validation argument
   */
  public allowedDecimals = (_context: LogicFnContext, allowedDecimalDigits: number): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string' || typeof value === 'number') {
      return value === '' || String(value).replace(DECIMAL_RX, '').replace(NUMERIC_ONLY_RX, '').length > allowedDecimalDigits;
    }
  };

  /**
   * Validate email
   * NOTE: Returns `true` if there is no value to check. Use in tandem with `required`
   */
  public email = (_context: LogicFnContext): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string') {
      return !value || !!value.match(SENSIBLE_EMAIL_RX);
    }
  };

  /**
   * Validate against a fixed number of digits
   * NOTE: Returns `true` if there is no value to check. Use in tandem with `required`
   * @param fixedDecimalDigits validation argument
   */
  public fixedDecimals = (_context: LogicFnContext, fixedDecimalDigits: number): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string' || typeof value === 'number') {
      return !value || String(value).replace(DECIMAL_RX, '').replace(NUMERIC_ONLY_RX, '').length !== fixedDecimalDigits;
    }
  };

  /**
   * Check if value is letter only
   */
  public lettersOnly = (_context: LogicFnContext): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string') {
      return !value || !!value.match(LETTERS_ONLY_RX);
    }
  };

  /**
   * Validate if a value between min and max
   * NOTE: Returns `true` if there is no value to check. Use in tandem with `required`
   * @param max validation argument
   * @param min validation argument
   */
  public between = (_context: LogicFnContext, min: number, max: number): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string' || typeof value === 'number') {
      const numValue = StatefulValue.numericValue(value);
      const numMin = StatefulValue.numericValue(min);
      const numMax = StatefulValue.numericValue(max);
      return !(value || value === 0) || (numMin <= numValue && numValue <= numMax);
    }
  };

  /**
   * Validate against a maximum
   * NOTE: Returns `true` if there is no value to check. Use in tandem with `required`
   * @param max validation argument
   */
  public max = (_context: LogicFnContext, max: number): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string' || typeof value === 'number') {
      return !(value || value === 0) || StatefulValue.numericValue(value) <= StatefulValue.numericValue(max);
    }
  };

  /**
   * Check value against a maximum length
   * NOTE: Returns `true` if there is no value to check. Use in tandem with `required`
   * @param maxLength validation argument
   */
  public maxLength = (_context: LogicFnContext, maxLength: number): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string' || typeof value === 'number') {
      return !(value || value === 0) || String(value).length <= maxLength;
    }
  };

  /**
   * Check value against a minimum
   * NOTE: Returns `true` if there is no value to check. Use in tandem with `required`
   * @param min validation argument
   */
  public min = (_context: LogicFnContext, min: number): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string' || typeof value === 'number') {
      return !(value || value === 0) || StatefulValue.numericValue(value) >= StatefulValue.numericValue(min);
    }
  };

  /**
   * Check value against a minimum length
   * NOTE: Returns `true` if there is no value to check. Use in tandem with `required`
   * @param minLength validation argument
   */
  public minLength = (_context: LogicFnContext, minLength: number): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string' || typeof value === 'number') {
      return !(value || value === 0) || String(value).length >= minLength;
    }
  };

  /**
   * Check if value is date and in the past
   */
  public past = (_context: LogicFnContext): boolean => {
    const value = this.getValue(_context);
    const validDate = this.date(_context);
    if (validDate && typeof value === 'string') {
      const date = new Date(this.dateString(value));
      return date <= new Date();
    }
  };

  /**
   * Date validation for future dates
   */
  public futureDate = (_context: LogicFnContext): boolean => {
    const value = this.getValue(_context);
    const validDate = this.date(_context);

    // eslint-disable-next-line eqeqeq
    if (value == null || value == '') {
      return true;
    }
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (validDate && typeof value === 'string') {
      const enteredDate = new Date(this.dateString(value));
      return enteredDate >= today;
    }
  };
  /**
   * Check if value is a date
   */
  public date = (_context: LogicFnContext): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string') {
      if (value.length > 0 && value.length <= 2) {
        return false;
      }
      if (value.split('/').length > 1) {
        return DATE_VALID_RX.test(value);
      }
    }
  };

  /**
   * Check if value is exactly `true`
   * @param value value to hold against the validation requirement
   */
  public requireTrue = (_context: LogicFnContext): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'boolean') {
      return value;
    }
  };

  /**
   * Check if value is non-empty
   * @param value value to hold against the validation requirement
   */
  public required = (_context: LogicFnContext): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string') {
      return !!value;
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      return value != null;
    } else if (value == null) {
      return false;
    }
  };

  /**
   * Check value is greater than
   * NOTE: Returns `true` if there is no value to check. Use in tandem with `required`
   * @param min validation argument
   */
  public greater = (_context: LogicFnContext, min: number): boolean => {
    const value = this.getValue(_context);
    if (typeof value === 'string' || typeof value === 'number') {
      return !(value || value === 0) || StatefulValue.numericValue(value) > StatefulValue.numericValue(min);
    }
  };

  private getValue = (_context: LogicFnContext): PrimitiveType => {
    const data = _context.value || (_context.field && _context.field.data);
    if (!data) {
      throw Error(`Validation '${_context.functionName}' used in a wrong context. It has to be used either in field or stateful context.`);
    }
    return data.value;
  };

  private dateString = (value: string): string => {
    const [month, day, year] = this.dateParts(value);
    return `${month}/${day}/${year}`;
  };

  private dateParts = (value: string): [number, number, number] => {
    const parts = value.split('/');
    const month = +parts.shift();
    let day = 1;
    let year = 0;
    if (parts.length === 1) {
      year = +parts.shift();
    } else {
      day = +parts.shift();
      year = +parts.shift();
    }
    return [month, day, year];
  };
}
